import { Radio, ShieldCheck } from 'lucide-react'
import type { NewsFeedState } from '../../hooks/useNewsFeed'
import { NewsEditionSwitch, type NewsEditionTab } from './NewsEditionSwitch'
import type { NewsExperienceMode } from './newsCuration'

const editionCopy: Record<NewsEditionTab, { eyebrow: string; heading: string; description: string; status: string }> = {
  ai: {
    eyebrow: 'AI · MODELS · COMPUTE · POWER',
    heading: 'AI publisher source monitor',
    description: 'Browse AI headlines from outside publishers and open their original reporting. This monitor is a discovery feed, separate from OMIKINA-created articles.',
    status: 'AI SIGNALS',
  },
  robots: {
    eyebrow: 'ROBOTICS · EMBODIED AI · MACHINES',
    heading: 'Robotics publisher source monitor',
    description: 'Browse externally published robotics headlines and open the original reporting. OMIKINA-created articles remain in the separate article archive.',
    status: 'ROBOTICS SIGNALS',
  },
  cybersecurity: {
    eyebrow: 'CYBERDEFENSE · THREATS · RESILIENCE',
    heading: 'Cybersecurity publisher source monitor',
    description: 'Browse outside reporting, advisories, and research on cyberdefense. These source links are separate from OMIKINA-created articles.',
    status: 'CYBERDEFENSE SIGNALS',
  },
}

const refreshLabel = (value: string | undefined) => value
  ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(new Date(value))
  : 'Waiting for the shared edition'

export function UnifiedNewsLanding({ activeEdition, feed, mode = 'full' }: { activeEdition: NewsEditionTab; feed?: NewsFeedState; mode?: NewsExperienceMode }) {
  if (mode === 'home') return <header className="unified-news-landing unified-news-landing--home" data-news-mode="home">
    <div className="unified-news-landing__copy">
      <span className="eyebrow">OMIKINA · ORIGINAL ARTICLES</span>
      <h1>Original analysis, with the evidence attached.</h1>
      <p>Read OMIKINA-created articles across AI, robotics, and cyberdefense. Each article names its author, cites its sources, and discloses its review status.</p>
      <p><a href="/articles">Browse every OMIKINA article</a> · <a href="/news">Open the separate publisher source monitor</a></p>
    </div>
  </header>
  const copy = editionCopy[activeEdition]
  const itemCount = feed?.data?.items.filter((item) => item.kind !== 'ai-synthesis').length ?? 0
  const sourceCount = feed?.data?.sources.length ?? 0

  return <header className={`unified-news-landing unified-news-landing--${activeEdition} unified-news-landing--${mode}`} data-news-mode={mode}>
    <div className="unified-news-landing__copy">
      <span className="eyebrow">OMIKINA · EXTERNAL PUBLISHER SOURCE MONITOR</span>
      <h1>{copy.heading}</h1>
      <p>{copy.description}</p>
    </div>
    <aside className="unified-news-landing__status" aria-live="polite">
      <Radio aria-hidden="true"/>
      <span>{copy.eyebrow}</span>
      <strong>{feed?.loading && !feed?.data ? 'CHECKING SOURCE WATCH' : feed?.data ? `${itemCount} ${copy.status}` : 'SOURCE WATCH UNAVAILABLE'}</strong>
      <small>{feed?.error ?? (feed?.data ? `${sourceCount} monitored sources · refreshed ${refreshLabel(feed.data.refreshedAt)}` : 'No story is fabricated when a source cache is unavailable.')}</small>
    </aside>
    <NewsEditionSwitch activeEdition={activeEdition}/>
    <p className="unified-news-landing__provenance"><ShieldCheck aria-hidden="true"/><span><strong>DISCOVERY, NOT OMIKINA AUTHORSHIP.</strong> These links lead to outside publishers. Read <a href="/articles">OMIKINA's original articles</a> in the separate archive.</span></p>
  </header>
}
